import { Box, BoxProps, Paper } from "@mui/material";
import { styled } from "@mui/material/styles";
import { MediaPlayer } from "JS/React/Components/MediaPlayer";
import { useMiniPlayer } from "JS/React/Hooks/MiniPlayer";
import { useEffect, useMemo, useRef, useState } from "react";
import { OnProgressProps } from "react-player/base";
import { useNavigate } from "react-router-dom";
import { MiniPlayerOverlay } from "./MiniPlayerOverlay";
import { checkIfUrlIsBlob } from "JS/Helpers";
import { getThumbnailUrl } from "JS/React/Hooks/Common/ImageUpload";
import { mediaService } from "JS/Services/Media";
import { config } from "JS/Config";
import { getAudioById } from "JS/Database/Audio";
import {
    Content,
    EventActions,
    EventCategories,
    EventNames,
    FirebaseEventAction,
    OfflineBundle,
    VideosAndGifts,
    ContentWithMedia,
    GiftWithMedia,
    ReceivedGift,
    ContentType,
} from "JS/Models";
import { useFirebaseLogger } from "JS/React/Hooks/Firebase";
import {
    getCategoryForType,
    getEventNameForType,
} from "JS/Helpers/MiniPlayerHelper";
import { useGlobalPlaylistControls } from "JS/React/Hooks/PlaylistControls";
import { shuffle } from "lodash-es";
import {
    useIsResumingAudio,
    useIsResumingBundle,
    useIsResumingVideo,
    useResumeRecentlyAudio,
    useResumeRecentlyBundles,
    useResumeRecentlyOfflineContent,
    useResumeRecentlyPlaylist,
    useResumeRecentlyVideo,
} from "JS/React/Hooks/ResumeRecently";
import {
    useDisclosureGate,
    usePublishDisclosurePlaying,
} from "JS/React/Hooks/Disclosure";
import { useDisclosureSequence } from "JS/React/Hooks/DisclosureSequence";
import {
    audiobookPartDisclosure,
    useIsAudiobook,
} from "JS/React/Hooks/Audiobook";
import FallbackWaveform from "Images/Content/audio-waves.png";
import Draggable, {
    ControlPosition,
    DraggableEventHandler,
} from "react-draggable";
import { isIOS } from "react-device-detect";
import { useGiftDashboard } from "JS/React/Hooks/Firebase/GiftDashboard";
import {
    useReceivedMemberAudioGifts,
    useReceivedMemberVideoGifts,
} from "JS/React/Hooks/Gifts";
import {
    getPlayerPlayedTimeToLog,
    toSentGift,
    toSentGiftType,
} from "JS/Models/Firebase/GiftDashboard";
import { useGlobalAudioDetails, useGlobalAudios } from "JS/React/Hooks/Audio";

interface StyledRootProps {
    isOffline?: boolean;
}

const StyledRoot = styled(Box, {
    shouldForwardProp: (prop) => prop !== "isOffline",
})<StyledRootProps>(({ theme, isOffline }) => ({
    position: "fixed",
    bottom: isOffline ? 0 : theme.footer.height,
    right: 0,
    zIndex: 1000,
    background: "grey",
    marginBottom: 5,
    marginRight: 5,
    borderRadius: "10px",
    height: "150px",
    width: "250px",
    [theme.breakpoints.up("sm")]: {
        height: "210px",
        width: "350px",
    },
    [theme.breakpoints.up("md")]: {
        height: "250px",
        width: "400px",
    },
    [theme.breakpoints.up("lg")]: {
        height: "270px",
        width: "450px",
    },
}));

const StyledContainer = styled(Paper)(({ theme }) => ({
    position: "relative",
    height: "100%",
    backgroundColor: theme.palette.background.default,
}));

export const MiniMediaPlayer = (props: MiniMediaPlayerProps) => {
    const { dragPosition, handleOnDrag, isOffline } = props;
    const nodeRef = useRef<HTMLDivElement>(null);

    const {
        url,
        restartToken,
        blockedMessage,
        isDisclosureSource,
        thumbnail,
        playerProgress,
        playerDuration,
        playing,
        title,
        isAudio,
        isLoop,
        playerRef,
        disableNavBtns,
        showNextPrevBtns,
        showButtons,
        isError,
        isReadyToPlay,
        setIsError,
        setPlaying,
        onClosePlayer,
        onMoveToParentScreen,
        handleProgress,
        onPlayerDuration,
        onTogglePlay,
        handleStart,
        handleReady,
        handleEnded,
        handleNext,
        handlePrevious,
        handlePlay,
        handlePause,
        setIsReadyToPlay,
        playlist,
        bundle,
    } = useHandlers(props);

    const imageUrl = useMemo(() => {
        if (thumbnail && thumbnail !== "a") return thumbnail;

        return FallbackWaveform;
    }, [thumbnail]);

    useEffect(() => {
        // EXTREMELY CRITICAL CODE, NEVER TO BE REMOVED
        setTimeout(() => {
            if (isIOS && (!!playlist || !!bundle)) setIsReadyToPlay(true);
        }, 1500);
    }, []);

    return (
        <Draggable
            nodeRef={nodeRef}
            position={dragPosition}
            onDrag={handleOnDrag}
            bounds={"parent"}
            cancel="#expand, #closemax, #previous, #playpause, #next"
        >
            <StyledRoot ref={nodeRef} isOffline={isOffline}>
                <StyledContainer elevation={2} variant={"elevation"}>
                    <MiniPlayerOverlay
                        isError={isError}
                        loading={!isReadyToPlay}
                        showButtons={showButtons}
                        playerProgress={playerProgress}
                        playerDuration={playerDuration}
                        playing={playing}
                        disableNavBtns={disableNavBtns}
                        title={title}
                        onClosePlayer={onClosePlayer}
                        onExpandClick={onMoveToParentScreen}
                        onPlayPauseClick={onTogglePlay}
                        onForwardClick={showNextPrevBtns ? handleNext : null}
                        onBackwardClick={
                            showNextPrevBtns ? handlePrevious : null
                        }
                    />

                    <MediaPlayer
                        isMini
                        title={title}
                        mediaURL={url}
                        restartToken={restartToken}
                        imageURL={imageUrl}
                        playing={playing}
                        isAudio={isAudio}
                        loop={isLoop}
                        playerRef={playerRef}
                        onProgress={handleProgress}
                        onDuration={onPlayerDuration}
                        onStart={handleStart}
                        onEnded={handleEnded}
                        onReady={handleReady}
                        onError={(e) => setIsError(true)}
                        onPlay={handlePlay}
                        onPause={handlePause}
                        isDisclosureSource={isDisclosureSource}
                        blockedMessage={blockedMessage}
                    />
                </StyledContainer>
            </StyledRoot>
        </Draggable>
    );
};

export const useHandlers = (props: MiniMediaPlayerProps) => {
    const navigate = useNavigate();
    const { playing, setPlaying } = props;

    const [disableNavBtns, setDisableNavBtns] = useState<boolean>(false);
    const [isError, setIsError] = useState(false);

    const {
        miniPlayer,
        setGlobalMiniPlayer,
        resetGlobalMiniPlayer,
        resetGlobalMiniOnClose,
    } = useMiniPlayer();

    const { handlePlayAnalytics } = useFirebaseAnalytics();
    const { showButtons, onNextMedia, onPrevMedia, onComplete } =
        useHandlePlaylistBundles();

    const vidResume = useResumeRecentlyVideo(
        miniPlayer.content as VideosAndGifts,
        miniPlayer.content?.nid,
    );
    const audResume = useResumeRecentlyAudio(
        miniPlayer.content as GiftWithMedia | ContentWithMedia,
        miniPlayer.content?.nid,
    );
    const bundleResume = useResumeRecentlyBundles(
        miniPlayer.bundleAudio?.data as any,
        miniPlayer.bundleAudio?.mediaIndex,
        true,
    );
    const playlistResume = useResumeRecentlyPlaylist(
        miniPlayer.playlist?.data,
        miniPlayer.playlist?.playlistId,
        miniPlayer.playlist?.mediaIndex,
        miniPlayer.playlist?.bundleIndex,
        true,
    );
    const offlineContentResume = useResumeRecentlyOfflineContent(
        miniPlayer.offlineContent,
        miniPlayer.isAudio ? "audio" : "video",
    );

    const resumeFuncs: ResumeFuncs = useMemo(() => {
        if (props.isOffline) return offlineContentResume;
        if (miniPlayer.bundleAudio) {
            return bundleResume;
        }

        if (miniPlayer.playlist) {
            return playlistResume;
        }

        if (miniPlayer.content) {
            if (
                ["GiftMP3", "MP3", "GiftProsMP3"].includes(
                    miniPlayer.contentType,
                )
            )
                return audResume;
            if (
                ["GiftVideo", "Video", "GiftProsVideo"].includes(
                    miniPlayer.contentType,
                )
            )
                return vidResume;
        }

        return {
            handleRecentlyPlayed: () => {},
            handleContentCompleted: () => {},
            onMediaPlayerReady: () => {},
            onPlayerProgress: () => {},
            setIsReady: () => {},
            setIsReadyToPlay: () => {},
        } as any;
    }, [
        props.isOffline,
        miniPlayer,
        vidResume?.isReadyToPlay,
        bundleResume?.isReadyToPlay,
        audResume?.isReadyToPlay,
        playlistResume?.isReadyToPlay,
        offlineContentResume?.isReadyToPlay,
    ]);

    const {
        playerRef,
        handleRecentlyPlayed,
        handleContentCompleted,
        onMediaPlayerReady,
        onPlayerProgress,
        setIsReady,
        isReadyToPlay,
        setIsReadyToPlay,
    } = resumeFuncs;

    const { getAudioDetails } = useGlobalAudios();

    const partMedia =
        "media" in miniPlayer?.content
            ? miniPlayer?.content?.media
            : getAudioDetails(miniPlayer?.content?.nid)?.media;
    const partIndex = !!miniPlayer?.playlist
        ? miniPlayer.playlist.bundleIndex
        : miniPlayer?.bundleAudio?.mediaIndex;

    const getPlayerAction = () => {
        const isBundle = partMedia?.length > 1;
        return getPlayerPlayedTimeToLog(
            playerRef,
            isBundle ? partIndex + 1 : undefined,
        );
    };

    const { isGift: isAudioGift } = useReceivedMemberAudioGifts(true);
    const { isGift: isVideoGift } = useReceivedMemberVideoGifts(true);
    const isReceivedMemberGift =
        isAudioGift(miniPlayer?.content?.nid) ||
        isVideoGift(miniPlayer?.content?.nid);

    const miniContentType = miniPlayer?.contentType as ContentType;
    const isResumingAud = useIsResumingAudio(miniPlayer?.content?.nid);
    const isResumingVid = useIsResumingVideo(miniPlayer?.content?.nid);
    // skipStart must read the same store the seek comes from
    const isResumingBundlePart = useIsResumingBundle(
        miniPlayer?.content?.nid,
        partMedia?.[partIndex]?.media_title,
    );

    // an audiobook handed to the mini player still discloses once around the whole book
    const isAudiobook = useIsAudiobook(miniPlayer?.content);

    const itemDisclosure = audiobookPartDisclosure({
        isAudiobook,
        disclosure: (miniPlayer?.content as ContentWithMedia)?.disclosure,
        partIndex,
        partCount: partMedia?.length,
    });

    const disclosure = useDisclosureGate({
        disclosure: itemDisclosure,
        skuId: miniPlayer?.content?.sku_id,
        nId: miniPlayer?.content?.nid,
        contentType: miniContentType,
    });

    const sequence = useDisclosureSequence({
        contentUrl: miniPlayer?.url,
        loop: miniPlayer?.singleLoop,
        disclosure: itemDisclosure,
        startUrl: disclosure.startUrl,
        endUrl: disclosure.endUrl,
        resolving: disclosure.resolving,
        skipStart:
            partMedia?.length > 1
                ? isResumingBundlePart
                : miniPlayer?.isAudio
                  ? isResumingAud
                  : isResumingVid,
        autoPlay: playing,
        analytics: disclosure.analytics,
    });

    usePublishDisclosurePlaying(sequence.isDisclosure);
    const { logGiftCompleted } = useGiftDashboard(
        toSentGift(
            miniPlayer?.content,
            miniPlayer?.bundleAudio?.mediaIndex ||
                miniPlayer?.playlist?.bundleIndex,
        ),
        toSentGiftType(miniPlayer?.contentType),
        "sender" in miniPlayer?.content
            ? miniPlayer?.content?.sender?.member_id
            : null,
        isReceivedMemberGift && playing,
        getPlayerAction,
    );

    const [playerProgress, setPlayerProgress] = useState<OnProgressProps>(null);
    const [playerDuration, setPlayerDuration] = useState<number>(null);

    const onClosePlayer = (
        e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    ) => {
        e.stopPropagation();
        setPlaying(false);
        resetGlobalMiniOnClose();
    };

    const onMoveToParentScreen = () => {
        const target = miniPlayer?.parentLink;
        if (target) {
            setPlaying(false);
            resetGlobalMiniPlayer(false);
            navigate(target);
        }
    };

    const onTogglePlay = () => {
        if (miniPlayer.forceStopPlaying)
            setGlobalMiniPlayer({
                forceStopPlaying: false,
            });

        if (sequence.playing) sequence.pause();
        else sequence.onPlay();

        setPlaying(!playing);
    };

    const handleProgress = (state: OnProgressProps) => {
        setPlayerProgress(state);
        if (
            state.playedSeconds > 5 &&
            Math.floor(state.playedSeconds) % 2 === 0 &&
            playing
        ) {
            onPlayerProgress(state);
        }
    };

    const onPlayerDuration = (duration: number) => {
        setPlayerDuration(duration);
    };

    const handleStart = () => {
        handlePlayAnalytics();
        handleRecentlyPlayed();
        onMediaPlayerReady();
    };

    const handleReady = () => {
        setDisableNavBtns(false);
        setIsError(false);
        setIsReadyToPlay(true);
    };

    const completeCurrentItem = () => {
        handleContentCompleted();
        if (isReceivedMemberGift) {
            const action = getPlayerAction();
            logGiftCompleted(
                !!action.index ? { index: action.index } : undefined,
            );
        }
    };

    const handleEnded = () => {
        const contentJustEnded = !sequence.isDisclosure;
        const sequenceDone = sequence.handleEnded();

        if (contentJustEnded) completeCurrentItem();
        if (sequenceDone) {
            const playNext = onComplete();
            setPlaying(playNext);
            if (playNext) sequence.resume();
            else sequence.pause();
        }
    };

    const handleNext = () => {
        setDisableNavBtns(true);
        onNextMedia(false);
        setPlaying(true);
        setIsReady(false);
    };

    const handlePrevious = () => {
        setDisableNavBtns(true);
        onPrevMedia();
        setPlaying(true);
        setIsReady(false);
    };

    const handlePlay = () => {
        sequence.onPlay();
        // only the content counts as the member playing; a disclosure must not reach
        // the gift dashboard or the resume position
        if (!sequence.isDisclosure) setPlaying(true);
        setGlobalMiniPlayer({
            forceStopPlaying: false,
        });
    };

    const handlePause = () => {
        sequence.onPause();
        if (!sequence.isDisclosure) setPlaying(false);
    };

    const handleStarted = () => {
        if (!sequence.isDisclosure) handleStart();
    };

    return {
        url: sequence.url,
        restartToken: sequence.stepToken,
        blockedMessage:
            disclosure.blocked || sequence.clipFailed
                ? disclosure.message
                : undefined,
        isDisclosureSource: sequence.isDisclosure,
        thumbnail: miniPlayer?.thumbnail,
        title: miniPlayer?.title,
        isAudio: miniPlayer?.isAudio,
        isLoop: sequence.isSequencing ? false : miniPlayer?.singleLoop,
        bundle: miniPlayer?.bundleAudio,
        playlist: miniPlayer?.playlist,
        playerProgress,
        playerDuration,
        playing: sequence.playing && !miniPlayer.forceStopPlaying,
        playerRef,
        disableNavBtns,
        showNextPrevBtns: !!miniPlayer?.bundleAudio || !!miniPlayer?.playlist,
        showButtons,
        isError,
        isReadyToPlay,
        setIsError,
        onTogglePlay,
        onClosePlayer,
        onMoveToParentScreen,
        handleProgress,
        onPlayerDuration,
        handleStart: handleStarted,
        handleReady,
        handleEnded,
        handleNext,
        handlePrevious,
        setPlaying,
        handlePlay,
        handlePause,
        setIsReadyToPlay,
    };
};

export const useHandlePlaylistBundles = () => {
    const { miniPlayer, setGlobalMiniPlayer } = useMiniPlayer();
    const { logFirebaseEvent } = useFirebaseLogger();

    const { playlistControls } = useGlobalPlaylistControls();

    const { playlist, bundleAudio } = miniPlayer;

    const [playData, setPlayData] = useState<
        (GiftWithMedia | ContentWithMedia)[]
    >([]);
    const [showButtons, setShowButtons] = useState(true);
    const { audioDetails } = useGlobalAudioDetails(miniPlayer?.content?.nid);
    const media = audioDetails?.media;
    const { isGift } = useReceivedMemberAudioGifts();

    // Preloaded URL for next track (fixes iOS background auto-play issue)
    const preloadedNextUrlRef = useRef<{
        key: string;
        url: string;
        title: string;
    } | null>(null);

    const currentBlobUrlRef = useRef<string | null>(null);

    const revokeBlobUrl = (url: string | null) => {
        if (url && checkIfUrlIsBlob(url)) {
            URL.revokeObjectURL(url);
        }
    };

    const cleanupPreloadedUrl = () => {
        if (preloadedNextUrlRef.current?.url) {
            revokeBlobUrl(preloadedNextUrlRef.current.url);
            preloadedNextUrlRef.current = null;
        }
    };

    useEffect(() => {
        if (!playlist) return;

        const { data, mediaIndex, bundleIndex } = playlist;

        const currentAud = data[mediaIndex];

        if (playlistControls?.shuffle) {
            const shuffled = shuffle(
                data.filter((a) => a.nid !== currentAud?.nid),
            );
            setGlobalMiniPlayer({
                playlist: {
                    ...playlist,
                    mediaIndex: 0,
                    bundleIndex: bundleIndex,
                },
            });
            setPlayData([currentAud, ...shuffled]);
        } else {
            let current = undefined;
            let indexInPlaylist = undefined;
            if (playData) current = playData[mediaIndex];
            if (current) {
                indexInPlaylist = data?.indexOf(
                    data?.find((a) => a.nid === current.nid),
                );
            }

            setGlobalMiniPlayer({
                playlist: {
                    ...playlist,
                    mediaIndex: indexInPlaylist ? indexInPlaylist : mediaIndex,
                    bundleIndex: bundleIndex,
                },
            });
            setPlayData([...playlist.data]);
        }
    }, [playlist?.data, playlistControls?.shuffle]);

    const fetchThumbnail = async () => {
        if (playlist) {
            const { mediaIndex } = playlist;
            const data = playData;

            const audio = data[mediaIndex];

            return await getThumbnailUrl(
                audio.image_url_postfix,
                audio.image_file_name,
            );
        }

        if (bundleAudio) {
            const { data, type } = bundleAudio;
            if (type === "online") {
                const audio = data as ReceivedGift | Content;
                return await getThumbnailUrl(
                    audio.image_url_postfix,
                    audio.image_file_name,
                );
            }
        }
        return "";
    };

    const getBundleMediaUrl = async () => {
        const { data, mediaIndex, type } = bundleAudio;

        if (media) {
            const { media_title } = media[mediaIndex];

            if (type === "online") {
                setShowButtons(false);
                const aud = data as ReceivedGift | Content;

                const contentNid = `${config.user.memberId}-${aud?.nid}-${
                    mediaIndex + 1
                }`;

                const isDwnlded = await checkAndSetIfDwnlded(
                    contentNid,
                    media_title,
                );

                const action: FirebaseEventAction = {
                    action: !isDwnlded
                        ? EventActions.LIVE_STREAM
                        : EventActions.PLAY,
                    nId: aud.nid,
                    contentTitle: media_title,
                    skuId: aud.sku_id,
                    category: getCategoryForType(miniPlayer.contentType),
                };

                logFirebaseEvent(
                    getEventNameForType(miniPlayer.contentType, isDwnlded),
                    action,
                );

                if (!isDwnlded) setIfNotDwnlded(media[mediaIndex]);
                else setShowButtons(true);
            }

            if (type === "offline") {
                const aud = data as OfflineBundle;
                const med = aud.media;

                const nid = aud?.id?.split("-")[1];

                const action: FirebaseEventAction = {
                    action: EventActions.PLAY,
                    nId: nid ? nid : "",
                    contentTitle: media_title,
                    skuId: aud.name,
                    category: EventCategories.AUDIOS,
                };

                logFirebaseEvent(EventNames.AUDIO_PLAYED, action);

                revokeBlobUrl(currentBlobUrlRef.current);
                const newBlobUrl = URL.createObjectURL(med[mediaIndex].blob);
                currentBlobUrlRef.current = newBlobUrl;

                setGlobalMiniPlayer({
                    url: newBlobUrl,
                    title: med[mediaIndex].media_title,
                });
            }
        }
    };

    const getPlaylistMediaUrl = async () => {
        const { mediaIndex, bundleIndex } = playlist;
        const data = playData;

        const audio = data[mediaIndex];
        if (audio) {
            setShowButtons(false);
            const isBundle = media?.length > 1;
            const contentNid = isBundle
                ? `${audio.nid}-${bundleIndex + 1}`
                : audio.nid;
            const contentKey = `${config.user.memberId}-${contentNid}`;

            const getTitle = () => {
                if (
                    !data ||
                    !Array.isArray(data) ||
                    mediaIndex < 0 ||
                    mediaIndex >= data.length ||
                    !data[mediaIndex]
                )
                    return "";
                const currentData: ContentWithMedia | GiftWithMedia =
                    data[mediaIndex];
                return currentData?.media?.length > 1
                    ? currentData?.media[bundleIndex]?.media_title || ""
                    : currentData?.title || "";
            };

            const title = getTitle();

            if (preloadedNextUrlRef.current?.key === contentKey) {
                const preloaded = preloadedNextUrlRef.current;

                revokeBlobUrl(currentBlobUrlRef.current);

                if (checkIfUrlIsBlob(preloaded.url)) {
                    currentBlobUrlRef.current = preloaded.url;
                } else {
                    currentBlobUrlRef.current = null;
                }

                setGlobalMiniPlayer({
                    url: preloaded.url,
                    title: preloaded.title,
                });
                setShowButtons(true);
                preloadedNextUrlRef.current = null;

                // Log analytics
                const isDownloaded = checkIfUrlIsBlob(preloaded.url);
                logFirebaseEvent(
                    getEventNameForType(miniPlayer.contentType, isDownloaded),
                    {
                        action: isDownloaded
                            ? EventActions.PLAY
                            : EventActions.LIVE_STREAM,
                        nId: audio.nid,
                        category: getCategoryForType(miniPlayer.contentType),
                        contentTitle: title,
                        skuId: audio.sku_id,
                    },
                );
                return;
            }

            const isDwnlded = await checkAndSetIfDwnlded(contentKey, title);

            const action: FirebaseEventAction = {
                action: !isDwnlded
                    ? EventActions.LIVE_STREAM
                    : EventActions.PLAY,
                nId: audio.nid,
                category: getCategoryForType(miniPlayer.contentType),
                contentTitle: title,
                skuId: audio.sku_id,
            };

            logFirebaseEvent(
                getEventNameForType(miniPlayer.contentType, isDwnlded),
                action,
            );

            if (!isDwnlded) {
                const {
                    media_url_prefix,
                    media_url_postfix,
                    media_file_name,
                    media_bucket_name,
                } = "media" in audio ? media[bundleIndex] : audio;

                setIfNotDwnlded({
                    media_url_prefix: media_url_prefix,
                    media_url_postfix: media_url_postfix,
                    media_file_name: media_file_name,
                    media_bucket_name: media_bucket_name,
                    media_title: title,
                });
            } else {
                setShowButtons(true);
            }
        }
    };

    const checkAndSetIfDwnlded = async (contentId: string, title: string) => {
        const dwnlded = await getAudioById(contentId);

        if (dwnlded) {
            revokeBlobUrl(currentBlobUrlRef.current);
            const newBlobUrl = URL.createObjectURL(dwnlded.blob);
            currentBlobUrlRef.current = newBlobUrl;

            setGlobalMiniPlayer({
                url: newBlobUrl,
                title: title,
            });

            return true;
        }
    };

    const setIfNotDwnlded = async (options: {
        media_url_prefix: string;
        media_url_postfix: string;
        media_file_name: string;
        media_bucket_name: string;
        media_title: string;
    }) => {
        const {
            media_url_postfix,
            media_file_name,
            media_bucket_name,
            media_title,
        } = options;

        if (!media_bucket_name || !media_url_postfix || !media_file_name) {
            return;
        }

        const thumb = await fetchThumbnail();
        mediaService
            .getSignedUrl(media_bucket_name, media_url_postfix, media_file_name)
            .then((response) => {
                setShowButtons(true);
                setGlobalMiniPlayer({
                    url: response.media_url,
                    title: media_title,
                    thumbnail: thumb,
                });
            })
            .catch((err) => console.log("Error fetching media URL:", err));
    };

    //internal helpers start from here
    const onNextPlaylist = (completed: boolean) => {
        if (!completed) {
            const { mediaIndex, bundleIndex } = miniPlayer.playlist;
            const { parentLink } = miniPlayer;
            const data = playData;

            const noOfAudios = media?.length;
            const isBundle = noOfAudios > 1;

            const newIndex =
                isBundle && bundleIndex < noOfAudios - 1
                    ? mediaIndex
                    : (mediaIndex + 1) % data?.length;
            const newBundleIndex =
                isBundle && bundleIndex < noOfAudios - 1 ? bundleIndex + 1 : 0;
            const nextAudio = data[newIndex];

            setGlobalMiniPlayer({
                ...miniPlayer,
                parentLink: parentLink?.replace(
                    `index=${mediaIndex}&bundleIndex=${bundleIndex}`,
                    `index=${newIndex}&bundleIndex=${newBundleIndex}`,
                ),
                playlist: {
                    ...playlist,
                    mediaIndex: newIndex,
                    bundleIndex: newBundleIndex,
                },
                content: nextAudio,
                contentType: isGift(nextAudio.nid)
                    ? "GiftMP3"
                    : nextAudio.isReceived
                      ? "GiftProsMP3"
                      : "MP3",
            });
        }
    };

    const onNextBundle = (completed: boolean) => {
        if (!completed) {
            const { mediaIndex } = miniPlayer.bundleAudio;
            const { parentLink } = miniPlayer;

            const noOfAudios = media?.length;
            const newMediaIndex = (mediaIndex + 1) % noOfAudios;

            setGlobalMiniPlayer({
                ...miniPlayer,
                parentLink: parentLink?.replace(
                    `index=${mediaIndex}`,
                    `index=${newMediaIndex}`,
                ),
                bundleAudio: {
                    ...miniPlayer.bundleAudio,
                    mediaIndex: newMediaIndex,
                },
            });
        }
    };

    const onPrevPlaylist = () => {
        cleanupPreloadedUrl();
        const { mediaIndex, bundleIndex } = miniPlayer.playlist;
        const { parentLink } = miniPlayer;
        const data = playData;
        const noOfAudios = media?.length;
        const isBundle = noOfAudios > 1;

        let newMediaIndex = mediaIndex;
        if (mediaIndex === 0) {
            if (isBundle) {
                if (bundleIndex === 0) newMediaIndex = data?.length - 1;
                else newMediaIndex = mediaIndex;
            } else newMediaIndex = data?.length - 1;
        } else if (!isBundle || bundleIndex === 0)
            newMediaIndex = mediaIndex - 1;

        let newBundleIndex = bundleIndex;
        if (isBundle && bundleIndex !== 0) {
            newBundleIndex = bundleIndex - 1;
        } else if (newMediaIndex !== mediaIndex) {
            const previous = data[newMediaIndex];
            const isBundle = +previous.no_of_files > 1;
            newBundleIndex = isBundle ? +previous?.no_of_files - 1 : 0;
        }
        const previous = data[newMediaIndex];

        setGlobalMiniPlayer({
            ...miniPlayer,
            parentLink: parentLink?.replace(
                `index=${mediaIndex}&bundleIndex=${bundleIndex}`,
                `index=${newMediaIndex}&bundleIndex=${newBundleIndex}`,
            ),
            playlist: {
                ...playlist,
                mediaIndex: newMediaIndex,
                bundleIndex: newBundleIndex,
            },
            content: previous,
            contentType: isGift(previous.nid)
                ? "GiftMP3"
                : previous.isReceived
                  ? "GiftProsMP3"
                  : "MP3",
        });
    };

    const onPrevBundle = () => {
        cleanupPreloadedUrl();
        const { mediaIndex } = miniPlayer.bundleAudio;
        const { parentLink } = miniPlayer;
        const newMediaIndex =
            mediaIndex === 0 ? media?.length - 1 : mediaIndex - 1;

        setGlobalMiniPlayer({
            ...miniPlayer,
            parentLink: parentLink?.replace(
                `index=${mediaIndex}`,
                `index=${newMediaIndex}`,
            ),
            bundleAudio: {
                ...miniPlayer?.bundleAudio,
                mediaIndex: newMediaIndex,
            },
        });
    };
    //internal helpers end here

    const onNextMedia = (completed: boolean) => {
        if (miniPlayer?.playlist) {
            onNextPlaylist(completed);
            return;
        }

        if (miniPlayer?.bundleAudio) {
            onNextBundle(completed);
            return;
        }
    };
    const onPrevMedia = () => {
        if (miniPlayer?.playlist) {
            onPrevPlaylist();
            return;
        }

        if (miniPlayer?.bundleAudio) {
            onPrevBundle();
            return;
        }
    };

    const onComplete = () => {
        let shouldPlayNext = false;
        if (miniPlayer?.playlist) {
            const { mediaIndex } = miniPlayer.playlist;
            const data = playData;
            const complete =
                playlistControls?.loop || mediaIndex === data?.length - 1;
            onNextMedia(complete);
            shouldPlayNext = !complete;
        } else if (miniPlayer?.bundleAudio) {
            const { mediaIndex } = miniPlayer?.bundleAudio;
            const complete = mediaIndex === media?.length - 1;
            onNextMedia(complete);
            shouldPlayNext = !complete;
        }
        return shouldPlayNext;
    };

    const preloadNextTrack = async () => {
        if (!playlist || !playData?.length) return;

        const { mediaIndex, bundleIndex } = playlist;
        const currentAudio = playData[mediaIndex];
        if (!currentAudio) return;

        const isCurrentBundle = media?.length > 1;
        let nextMediaIndex = mediaIndex;
        let nextBundleIndex = bundleIndex;

        if (isCurrentBundle && bundleIndex < media.length - 1) {
            nextBundleIndex = bundleIndex + 1;
        } else {
            nextMediaIndex = (mediaIndex + 1) % playData.length;
            nextBundleIndex = 0;
        }

        const nextAudio = playData[nextMediaIndex];
        if (!nextAudio) return;

        const nextIsBundle = +nextAudio.no_of_files > 1;
        const contentNid = nextIsBundle
            ? `${nextAudio.nid}-${nextBundleIndex + 1}`
            : nextAudio.nid;
        const key = `${config.user.memberId}-${contentNid}`;

        if (preloadedNextUrlRef.current?.key === key) return;

        cleanupPreloadedUrl();

        const title = nextIsBundle
            ? nextAudio?.media?.[nextBundleIndex]?.media_title || ""
            : nextAudio?.title || "";

        try {
            const downloaded = await getAudioById(key);
            if (downloaded?.blob) {
                preloadedNextUrlRef.current = {
                    key,
                    url: URL.createObjectURL(downloaded.blob),
                    title,
                };
                return;
            }

            if ("media" in nextAudio && nextAudio.media?.[nextBundleIndex]) {
                const {
                    media_url_postfix,
                    media_file_name,
                    media_bucket_name,
                } = nextAudio.media[nextBundleIndex];

                if (media_bucket_name && media_url_postfix && media_file_name) {
                    const response = await mediaService.getSignedUrl(
                        media_bucket_name,
                        media_url_postfix,
                        media_file_name,
                    );
                    preloadedNextUrlRef.current = {
                        key,
                        url: response.media_url,
                        title,
                    };
                }
            }
        } catch (e) {
            // silently fail preloading
        }
    };

    useEffect(() => {
        return () => {
            cleanupPreloadedUrl();
            revokeBlobUrl(currentBlobUrlRef.current);
            currentBlobUrlRef.current = null;
        };
    }, []);

    useEffect(() => {
        if (playlist && playData?.length > 1) {
            preloadNextTrack();
        }
    }, [playlist?.mediaIndex, playlist?.bundleIndex, playData]);

    useEffect(() => {
        if (playlist && playData) {
            getPlaylistMediaUrl();
        }
    }, [playlist?.bundleIndex, playlist?.mediaIndex, playData, media]);

    useEffect(() => {
        if (bundleAudio) {
            getBundleMediaUrl();
        }
    }, [bundleAudio?.data, bundleAudio?.mediaIndex]);

    return {
        isPlaylistOrBundle: miniPlayer?.playlist || miniPlayer?.bundleAudio,
        showButtons,
        onNextMedia,
        onPrevMedia,
        onComplete,
    };
};

const useFirebaseAnalytics = () => {
    const { logFirebaseEvent } = useFirebaseLogger();
    const { miniPlayer } = useMiniPlayer();

    const isDownloaded = checkIfUrlIsBlob(miniPlayer.url);
    const recommendation = miniPlayer.recommendation;

    const handlePlayAnalytics = () => {
        if (miniPlayer?.content) {
            const { nid, sku_id, title } = miniPlayer.content;

            const action: FirebaseEventAction = {
                action: isDownloaded
                    ? EventActions.PLAY
                    : EventActions.LIVE_STREAM,
                nId: nid,
                contentTitle: title,
                skuId: sku_id,
                category: getCategoryForType(miniPlayer.contentType),
            };

            if (recommendation) {
                action.category = EventCategories.RECOMMENDATIONS;
                action.displayCount = recommendation.displayCount;
                action.source = recommendation.source;
                action.recId = `${recommendation.recId}`;
            }

            logFirebaseEvent(
                getEventNameForType(
                    miniPlayer.contentType,
                    isDownloaded,
                    !!recommendation,
                ),
                action,
            );
        }
    };

    return {
        handlePlayAnalytics,
    };
};

export interface MiniMediaPlayerProps extends BoxProps {
    isOffline?: boolean;
    playing: boolean;
    dragPosition: ControlPosition;
    handleOnDrag: DraggableEventHandler;
    setPlaying: React.Dispatch<React.SetStateAction<boolean>>;
}

export type ResumeFuncs = {
    playerRef: React.MutableRefObject<any>;
    isReady: boolean;
    setIsReady: React.Dispatch<React.SetStateAction<boolean>>;
    onMediaPlayerReady: () => void;
    onPlayerProgress: (state: OnProgressProps) => Promise<any>;
    handleContentCompleted: () => void;
    handleRecentlyPlayed: () => void;
    isReadyToPlay?: boolean;
    setIsReadyToPlay?: React.Dispatch<React.SetStateAction<boolean>>;
};
